import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { apiCall } from '../api/client';

export default function RequestDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const selectMutation = useMutation({
    mutationFn: (matchId: string) => apiCall(`/matches/${matchId}/select`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['request', id] }),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['request', id],
    queryFn: () => apiCall<any>(`/requests/${id}`),
    refetchInterval: 10000,
  });

  if (isLoading) return <div className="page" style={{ textAlign: 'center' }}><div className="doe-spinner" style={{ margin: '48px auto' }} /></div>;

  const request = data?.data;
  if (!request) return <div className="page"><p>Request not found</p></div>;

  const acceptedMatches = (request.matches || []).filter((m: any) => ['accepted', 'selected'].includes(m.status));

  return (
    <div className="page" style={{ maxWidth: 700, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 'var(--space-lg)' }}>
        {request.trade_icon} {request.trade_name} — {request.problem_label || 'Service Request'}
      </h1>

      <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-sm)' }}>
          <div><strong>Status:</strong> <span className={`badge badge-${request.status === 'completed' ? 'online' : 'pending'}`}>{request.status}</span></div>
          <div><strong>Urgency:</strong> <span className={`badge urgency-${request.urgency?.replace('_', '-')}`}>{request.urgency}</span></div>
          <div><strong>Budget:</strong> {request.budget_range?.replace(/_/g, ' ')}</div>
          {request.description && <div><strong>Details:</strong> {request.description}</div>}
        </div>
      </div>

      <h2 style={{ fontSize: '1.3rem', fontWeight: 600, marginBottom: 'var(--space-md)' }}>
        Contractors ({acceptedMatches.length}/3 responded)
      </h2>

      {acceptedMatches.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ fontSize: '2rem', marginBottom: 8 }}>⏳</p>
          <p>Waiting for contractors to respond...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          {acceptedMatches.map((match: any) => (
            <div key={match.id} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--color-tan)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                  {match.display_name?.[0] || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontWeight: 600 }}>{match.business_name || match.display_name}</h3>
                  <div className="stars">{'★'.repeat(Math.round(match.avg_rating || 0))}{'☆'.repeat(5 - Math.round(match.avg_rating || 0))} ({match.total_reviews || 0})</div>
                </div>
                {match.quote_amount && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-brown)' }}>${match.quote_amount}</div>
                    {match.ai_baseline_amount && (
                      <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Market avg: ${match.ai_baseline_amount}</div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <Link to={`/chat?request=${request.id}&contractor=${match.contractor_id}`}>
                  <button className="chip">💬 Chat</button>
                </Link>
                {match.status !== 'selected' && match.quote_amount && (
                  <button
                    className="btn-primary"
                    style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                    onClick={() => selectMutation.mutate(match.id)}
                    disabled={selectMutation.isPending}
                  >
                    {selectMutation.isPending ? 'Selecting...' : 'Select This Doe'}
                  </button>
                )}
                {match.status === 'selected' && <span className="badge badge-online">✓ Selected</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="disclaimer" style={{ marginTop: 'var(--space-xl)' }}>
        AI-generated pricing is subject to error. WorkDoe is not responsible for the quality, safety, or completion of any work performed.
      </div>
    </div>
  );
}
